// QpayCenterSettlementsReport.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { formatDateTimeIST } from '../../utils/dateFormat';

const QpayCenterSettlementsReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = usePermissions();
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState('');

  // Dropdown options
  const [allStatesList, setAllStatesList] = useState(['Kerala']);
  const [allDistrictsList, setAllDistrictsList] = useState([]);
  const [rawTaluksList, setRawTaluksList] = useState([]);
  const [allConstituenciesList, setAllConstituenciesList] = useState([]);
  const [qpayCentersList, setQpayCentersList] = useState([]);

  // Filter States
  const [stateFilter, setStateFilter] = useState('all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all');
  const [qpayCenterFilter, setQpayCenterFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' or 'merchant'
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  const token = sessionStorage.getItem('token');

  // Decode token
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, [token]);

  // Fetch dropdown lists
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const stateRes = await axios.get(`${API_BASE_URL}/states`);
        if (stateRes.status === 200 && stateRes.data) {
          const list = Array.isArray(stateRes.data)
            ? stateRes.data
            : stateRes.data.states || [];
          const sNames = list.map((s) => s.stateName || s.name).filter(Boolean);
          if (sNames.length > 0) setAllStatesList(sNames);
        }
      } catch (err) {
        console.error('Error fetching states dropdown:', err);
      }

      try {
        const distRes = await axios.get(`${API_BASE_URL}/districts`);
        if (distRes.status === 200 && distRes.data) {
          const list = Array.isArray(distRes.data)
            ? distRes.data
            : distRes.data.districts || [];
          setAllDistrictsList(list);
        }
      } catch (err) {
        console.error('Error fetching districts dropdown:', err);
      }

      try {
        const talukRes = await axios.get(`${API_BASE_URL}/taluks`);
        if (talukRes.status === 200 && talukRes.data) {
          const list = Array.isArray(talukRes.data)
            ? talukRes.data
            : talukRes.data.taluks || [];
          setRawTaluksList(list);
          const names = [...new Set(list.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
          setAllConstituenciesList(names.sort());
        }
      } catch (err) {
        console.error('Error fetching constituencies dropdown:', err);
      }

      try {
        const qcRes = await axios.post(`${API_BASE_URL}/qpay-centers/all`, { userId });
        if (qcRes.status === 200 && qcRes.data && qcRes.data.data) {
          setQpayCentersList(qcRes.data.data);
        }
      } catch (err) {
        console.error('Error fetching Q-Pay Centers dropdown:', err);
      }
    };

    fetchDropdownData();
  }, [userId]);

  // Update Constituency dropdown whenever District filter changes
  useEffect(() => {
    setVillageFilter('all');
    if (districtFilter === 'all') {
      const names = [...new Set(rawTaluksList.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    } else {
      const selectedDistObj = allDistrictsList.find(
        (d) => (d.districtName || d.name || '').toLowerCase() === districtFilter.toLowerCase()
      );
      const selectedDistId = selectedDistObj ? String(selectedDistObj._id || selectedDistObj.id) : null;

      const filteredTaluks = rawTaluksList.filter((t) => {
        const tDistId = String(t.district || t.districtId || '');
        const tDistName = (t.districtName || '').toLowerCase();
        return (
          (selectedDistId && tDistId === selectedDistId) ||
          tDistName === districtFilter.toLowerCase()
        );
      });

      const names = [...new Set(filteredTaluks.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    }
  }, [districtFilter, allDistrictsList, rawTaluksList]);

  // Fetch Settlements Report
  const fetchSettlementsReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        userId,
        startDate,
        endDate,
        state: stateFilter,
        district: districtFilter,
        village: villageFilter,
        qpayCenterId: qpayCenterFilter,
        filterType: typeFilter,
        search: searchQuery,
        sortOrder
      };

      const response = await axios.post(
        `${API_BASE_URL}/qpay-centers/settlements-report`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.success) {
        setReportData(response.data.data || []);
      } else {
        setError('Failed to fetch settlements report.');
      }
    } catch (err) {
      console.error('Error fetching settlements report:', err);
      setError('An error occurred while fetching the settlements report.');
    } finally {
      setLoading(false);
    }
  }, [
    userId,
    startDate,
    endDate,
    stateFilter,
    districtFilter,
    villageFilter,
    qpayCenterFilter,
    typeFilter,
    searchQuery,
    sortOrder,
    token
  ]);

  useEffect(() => {
    if (userId) {
      fetchSettlementsReport();
    }
  }, [userId, fetchSettlementsReport]);

  const handleResetFilters = () => {
    setStateFilter('all');
    setDistrictFilter('all');
    setVillageFilter('all');
    setQpayCenterFilter('all');
    setTypeFilter('all');
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setSortOrder('desc');
  };

  // CSV Export for Level 1 Super Admin ONLY
  const handleExportCSV = () => {
    if (!can('qpayCentre.settlementsReport.export')) return;
    if (reportData.length === 0) {
      alert('No data available to export.');
      return;
    }

    const headers = [
      'SL No',
      'Date',
      'Q-Pay Center ID',
      'Settled Amount',
      'Transaction ID',
      'Remarks'
    ];

    const rows = reportData.map((row) => [
      row.slNo,
      `"${formatDateTimeIST(row.date)}"`,
      `"${row.qpayCenterCustomId || 'N/A'}"`,
      row.settledAmount || 0,
      `"${row.transactionId || 'N/A'}"`,
      `"${row.remarks || 'N/A'}"`
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `qpay_centre_settlements_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackButton />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', ml: 1 }}>
          Q-PAY Centre Settlements Report
        </Typography>
      </Box>

      {/* Filters Box */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          {/* State Wise */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>State wise</InputLabel>
              <Select
                value={stateFilter}
                label="State wise"
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <MenuItem value="all">All States</MenuItem>
                {allStatesList.map((st) => (
                  <MenuItem key={st} value={st}>
                    {st}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* District Wise */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>District wise</InputLabel>
              <Select
                value={districtFilter}
                label="District wise"
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                {allDistrictsList.map((d) => {
                  const dName = d.districtName || d.name;
                  return (
                    <MenuItem key={d._id || dName} value={dName}>
                      {dName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Constituency Wise */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Constituency wise</InputLabel>
              <Select
                value={villageFilter}
                label="Constituency wise"
                onChange={(e) => setVillageFilter(e.target.value)}
              >
                {allConstituenciesList.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Q-Pay Centre Wise */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Q-Pay Centre wise</InputLabel>
              <Select
                value={qpayCenterFilter}
                label="Q-Pay Centre wise"
                onChange={(e) => setQpayCenterFilter(e.target.value)}
              >
                <MenuItem value="all">All Q-Pay Centres</MenuItem>
                {qpayCentersList.map((qc) => (
                  <MenuItem key={qc.id} value={qc.id}>
                    {qc.customId} - {qc.firstName} {qc.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Type: All / Merchant */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by</InputLabel>
              <Select
                value={typeFilter}
                label="Filter by"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="merchant">Merchant</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>

          {/* Search Bar */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="Search Q-Pay Center / Txn ID / Remarks"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon color="action" fontSize="small" />
              }}
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              startIcon={<RefreshIcon />}
              fullWidth
              size="small"
              sx={{ height: 40, borderRadius: 2 }}
            >
              Reset
            </Button>
            {can('qpayCentre.settlementsReport.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                fullWidth
                size="small"
                sx={{ height: 40, fontWeight: 700, borderRadius: 2 }}
              >
                CSV
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  SL No
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Q-Pay Center ID
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Settled Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Transaction ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Remarks
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#64748b' }}>
                    No settlement records found for the selected criteria.
                  </TableCell>
                </TableRow>
              ) : (
                reportData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.slNo}</TableCell>
                    <TableCell>{formatDateTimeIST(row.date)}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2563eb' }}>
                      {row.qpayCenterCustomId} {row.qpayCenterName ? `(${row.qpayCenterName})` : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#059669' }}>
                      ₹{(row.settledAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {row.transactionId}
                    </TableCell>
                    <TableCell sx={{ color: '#475569' }}>{row.remarks}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default QpayCenterSettlementsReport;
